/**
 * CEL signature help provider for showing function parameter hints.
 *
 * When typing inside a function call, displays the function signature
 * with the current parameter highlighted.
 *
 * @module cel/signatureProvider
 */

import type monaco from 'monaco-editor'

import type { SchemaRegistry } from './types'
import { parseFunctionContext } from './utils'

/**
 * Create a Monaco signature help provider for CEL functions.
 *
 * Triggered by `(` and `,` characters. Parses the current line to find
 * the enclosing function call and displays its signature with the
 * active parameter highlighted.
 *
 * @param schemaRegistry - Registry mapping model URIs to CEL schemas
 * @returns Monaco SignatureHelpProvider instance
 *
 * @example
 * ```ts
 * const schemaRegistry = new Map<string, CELSchema>()
 * const sigProvider = makeCELSignatureHelpProvider(schemaRegistry)
 * monaco.languages.registerSignatureHelpProvider('cel', sigProvider)
 *
 * // Typing "c1.user.v1.HasEntitlement(subject, |" shows:
 * // HasEntitlement(c1.user.v1.User, string) → bool
 * //                                 ^^^^^^ (highlighted)
 * ```
 */
export const makeCELSignatureHelpProvider = (
    schemaRegistry: SchemaRegistry,
): monaco.languages.SignatureHelpProvider => ({
    signatureHelpTriggerCharacters: ['(', ','],
    signatureHelpRetriggerCharacters: [','],
    provideSignatureHelp: (model, position): monaco.languages.SignatureHelpResult | undefined => {
        const schema = schemaRegistry.get(model.uri.toString())
        if (!schema) return undefined

        const line = model.getLineContent(position.lineNumber)
        const lineUpToPosition = line.slice(0, position.column - 1)

        const { functionName, activeParameter } = parseFunctionContext(lineUpToPosition)
        if (!functionName) return undefined

        // Find the function in the schema
        const fn = schema.functions.find((f) => f.name === functionName)
        if (!fn) return undefined

        const signature: monaco.languages.SignatureInformation = {
            label: `${fn.name}(${fn.paramTypes.join(', ')}) → ${fn.returnType}`,
            documentation: fn.description || undefined,
            parameters: fn.paramTypes.map((paramType, i): monaco.languages.ParameterInformation => ({
                label: paramType,
                documentation: `Parameter ${i + 1}: ${paramType}`,
            })),
        }

        return {
            value: {
                signatures: [signature],
                activeSignature: 0,
                activeParameter: Math.max(0, Math.min(activeParameter, fn.paramTypes.length - 1)),
            },
            dispose: (): void => { /* no-op, required by interface */ },
        }
    },
})
